import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "p-4 sm:p-6" }
const _hoisted_2 = { class: "mb-6 sm:mb-8 flex justify-between items-center" }
const _hoisted_3 = { class: "flex gap-3" }
const _hoisted_4 = { class: "bg-white dark:bg-black overflow-hidden mb-6" }
const _hoisted_5 = { class: "p-6" }
const _hoisted_6 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_7 = { class: "space-y-4" }
const _hoisted_8 = { class: "text-lg font-semibold text-gray-900 dark:text-white flex items-center" }
const _hoisted_9 = { class: "space-y-2" }
const _hoisted_10 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_11 = { class: "mt-1 text-gray-900 dark:text-white font-medium" }
const _hoisted_12 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_13 = { class: "mt-1 text-gray-900 dark:text-white font-medium" }
const _hoisted_14 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_15 = { class: "mt-1 text-gray-900 dark:text-white font-medium" }
const _hoisted_16 = { class: "space-y-4" }
const _hoisted_17 = { class: "text-lg font-semibold text-gray-900 dark:text-white flex items-center" }
const _hoisted_18 = { class: "space-y-2" }
const _hoisted_19 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_20 = { class: "mt-1 text-gray-900 dark:text-white font-medium break-all" }
const _hoisted_21 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_22 = { class: "mt-1 text-gray-900 dark:text-white font-medium" }
const _hoisted_23 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_24 = { class: "mt-1 text-gray-900 dark:text-white font-medium capitalize" }
const _hoisted_25 = { class: "mt-6" }
const _hoisted_26 = { class: "text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4" }
const _hoisted_27 = { class: "grid grid-cols-1 md:grid-cols-2 gap-3" }
const _hoisted_28 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_29 = { class: "mt-1 text-gray-900 dark:text-white font-medium" }
const _hoisted_30 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_31 = { class: "mt-1 text-gray-900 dark:text-white font-medium" }
const _hoisted_32 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_33 = { class: "mt-1 text-gray-900 dark:text-white font-medium" }
const _hoisted_34 = { class: "border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2" }
const _hoisted_35 = { class: "mt-1 text-gray-900 dark:text-white font-medium" }

import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useBrand } from '@/composables/brand/useBrand'
import { customerProfileService } from '@/services/api/customerProfileService'
import type { ProfileUpdateData } from '@/services/api/customerProfileService'
import { useToast } from 'vue-toastification'
import EditProfileModal from '@/components/customer/EditProfileModal.vue'
import ChangePasswordModal from '@/components/customer/ChangePasswordModal.vue'
import api from '@/services/api'


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomerInfoView',
  setup(__props) {

const toast = useToast()

// Composables
const authStore = useAuthStore()
const { brandColors } = useBrand()

// Local state
const showEditModal = ref(false)
const showChangePasswordModal = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)

// Computed Properties
const currentUser = computed(() => authStore.currentUser)

// Methods
function openEditModal() {
  showEditModal.value = true
  error.value = null
}

function closeEditModal() {
  showEditModal.value = false
  error.value = null
}

async function handleUpdateProfile(data: ProfileUpdateData) {
  loading.value = true
  error.value = null

  try {
    const response = await customerProfileService.updateProfile(data)

    // Update the auth store with the new user data from the response
    if (response.data) {
      authStore.updateUserData(response.data)
    }

    toast.success('Profile updated successfully')
    closeEditModal()
  } catch (err: any) {
    // Extract user-friendly error message
    let errorMessage = 'Failed to update profile'

    if (err.response?.data?.message) {
      // Use the specific error message from backend
      errorMessage = err.response.data.message
    } else if (err.response?.data?.errors) {
      // If there are field-specific errors, get the first one
      const errors = err.response.data.errors
      const firstErrorKey = Object.keys(errors)[0]
      if (firstErrorKey && errors[firstErrorKey]) {
        errorMessage = Array.isArray(errors[firstErrorKey])
          ? errors[firstErrorKey][0]
          : errors[firstErrorKey]
      }
    }

    error.value = errorMessage
    toast.error(errorMessage)

    // Keep modal open so user can see the error and fix it
  } finally {
    loading.value = false
  }
}

function openChangePasswordModal() {
  showChangePasswordModal.value = true
  error.value = null
}

function closeChangePasswordModal() {
  showChangePasswordModal.value = false
  error.value = null
}

async function handleChangePassword(data: { currentPassword: string; newPassword: string }) {
  loading.value = true
  error.value = null

  try {
    const response = await api.put('/customer/change-password', {
      current_password: data.currentPassword,
      new_password: data.newPassword
    })

    toast.success('Password changed successfully')
    closeChangePasswordModal()
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || 'Failed to change password'
    error.value = errorMessage
    toast.error(errorMessage)
  } finally {
    loading.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[1] || (_cache[1] = _createElementVNode("h1", { class: "text-2xl font-bold text-gray-900 dark:text-white" }, "Customer Information", -1)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("button", {
            onClick: openChangePasswordModal,
            class: "px-4 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-white flex items-center gap-2",
            style: _normalizeStyle({ backgroundColor: _unref(brandColors).accent })
          }, _cache[0] || (_cache[0] = [
            _createElementVNode("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 24 24",
              fill: "currentColor",
              class: "w-5 h-5"
            }, [
              _createElementVNode("path", {
                "fill-rule": "evenodd",
                d: "M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z",
                "clip-rule": "evenodd"
              })
            ], -1),
            _createTextVNode(" Change Password ")
          ]), 4),
          _createElementVNode("button", {
            onClick: openEditModal,
            class: "px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-white",
            style: _normalizeStyle({ backgroundColor: _unref(brandColors).primary })
          }, " Edit Profile ", 4)
        ])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("h3", _hoisted_8, [
                (_openBlock(), _createElementBlock("svg", {
                  class: "w-5 h-5 mr-2",
                  style: _normalizeStyle({ color: _unref(brandColors).accent }),
                  fill: "currentColor",
                  viewBox: "0 0 20 20"
                }, _cache[2] || (_cache[2] = [
                  _createElementVNode("path", {
                    "fill-rule": "evenodd",
                    d: "M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z",
                    "clip-rule": "evenodd"
                  }, null, -1)
                ]), 4)),
                _cache[3] || (_cache[3] = _createTextVNode(" Personal Information "))
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("label", {
                    class: "text-xs font-medium uppercase tracking-wide",
                    style: _normalizeStyle({ color: _unref(brandColors).primary })
                  }, "First Name", 4),
                  _createElementVNode("p", _hoisted_11, _toDisplayString(currentUser.value?.first_name || 'N/A'), 1)
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("label", {
                    class: "text-xs font-medium uppercase tracking-wide",
                    style: _normalizeStyle({ color: _unref(brandColors).primary })
                  }, "Last Name", 4),
                  _createElementVNode("p", _hoisted_13, _toDisplayString(currentUser.value?.last_name || 'N/A'), 1)
                ]),
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("label", {
                    class: "text-xs font-medium uppercase tracking-wide",
                    style: _normalizeStyle({ color: _unref(brandColors).primary })
                  }, "Brand", 4),
                  _createElementVNode("p", _hoisted_15, _toDisplayString(currentUser.value?.brand_name || 'N/A'), 1)
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_16, [
              _createElementVNode("h3", _hoisted_17, [
                (_openBlock(), _createElementBlock("svg", {
                  class: "w-5 h-5 mr-2",
                  style: _normalizeStyle({ color: _unref(brandColors).accent }),
                  fill: "currentColor",
                  viewBox: "0 0 20 20"
                }, _cache[4] || (_cache[4] = [
                  _createElementVNode("path", { d: "M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" }, null, -1),
                  _createElementVNode("path", { d: "M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" }, null, -1)
                ]), 4)),
                _cache[5] || (_cache[5] = _createTextVNode(" Contact Information "))
              ]),
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("label", {
                    class: "text-xs font-medium uppercase tracking-wide",
                    style: _normalizeStyle({ color: _unref(brandColors).primary })
                  }, "Email Address", 4),
                  _createElementVNode("p", _hoisted_20, _toDisplayString(currentUser.value?.email || 'N/A'), 1)
                ]),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("label", {
                    class: "text-xs font-medium uppercase tracking-wide",
                    style: _normalizeStyle({ color: _unref(brandColors).primary })
                  }, "Mobile Number", 4),
                  _createElementVNode("p", _hoisted_22, _toDisplayString(currentUser.value?.mobile || 'N/A'), 1)
                ]),
                _createElementVNode("div", _hoisted_23, [
                  _createElementVNode("label", {
                    class: "text-xs font-medium uppercase tracking-wide",
                    style: _normalizeStyle({ color: _unref(brandColors).primary })
                  }, "Account Status", 4),
                  _createElementVNode("p", _hoisted_24, _toDisplayString(currentUser.value?.status || 'N/A'), 1)
                ])
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_25, [
            _createElementVNode("h3", _hoisted_26, [
              (_openBlock(), _createElementBlock("svg", {
                class: "w-5 h-5 mr-2",
                style: _normalizeStyle({ color: _unref(brandColors).accent }),
                fill: "currentColor",
                viewBox: "0 0 20 20"
              }, _cache[6] || (_cache[6] = [
                _createElementVNode("path", {
                  "fill-rule": "evenodd",
                  d: "M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z",
                  "clip-rule": "evenodd"
                }, null, -1)
              ]), 4)),
              _cache[7] || (_cache[7] = _createTextVNode(" Address Information "))
            ]),
            _createElementVNode("div", _hoisted_27, [
              _createElementVNode("div", _hoisted_28, [
                _createElementVNode("label", {
                  class: "text-xs font-medium uppercase tracking-wide",
                  style: _normalizeStyle({ color: _unref(brandColors).primary })
                }, "House No.", 4),
                _createElementVNode("p", _hoisted_29, _toDisplayString(currentUser.value?.house_no || 'N/A'), 1)
              ]),
              _createElementVNode("div", _hoisted_30, [
                _createElementVNode("label", {
                  class: "text-xs font-medium uppercase tracking-wide",
                  style: _normalizeStyle({ color: _unref(brandColors).primary })
                }, "Street", 4),
                _createElementVNode("p", _hoisted_31, _toDisplayString(currentUser.value?.street || 'N/A'), 1)
              ]),
              _createElementVNode("div", _hoisted_32, [
                _createElementVNode("label", {
                  class: "text-xs font-medium uppercase tracking-wide",
                  style: _normalizeStyle({ color: _unref(brandColors).primary })
                }, "Barangay", 4),
                _createElementVNode("p", _hoisted_33, _toDisplayString(currentUser.value?.barangay || 'N/A'), 1)
              ]),
              _createElementVNode("div", _hoisted_34, [
                _createElementVNode("label", {
                  class: "text-xs font-medium uppercase tracking-wide",
                  style: _normalizeStyle({ color: _unref(brandColors).primary })
                }, "City", 4),
                _createElementVNode("p", _hoisted_35, _toDisplayString(currentUser.value?.city || 'N/A'), 1)
              ])
            ])
          ])
        ])
      ])
    ]),
    _createVNode(EditProfileModal, {
      "is-open": showEditModal.value,
      "user-profile": currentUser.value,
      loading: loading.value,
      error: error.value,
      onClose: closeEditModal,
      onSubmit: handleUpdateProfile
    }, null, 8, ["is-open", "user-profile", "loading", "error"]),
    _createVNode(ChangePasswordModal, {
      "is-open": showChangePasswordModal.value,
      loading: loading.value,
      error: error.value,
      onClose: closeChangePasswordModal,
      onSubmit: handleChangePassword
    }, null, 8, ["is-open", "loading", "error"])
  ], 64))
}
}

})